import * as React from 'react';
import './TreeItemGroup.scss';

export type TreeItemGroupProps = {
  in?: boolean;
  children?: React.ReactNode;
  component?: React.ElementType;
  unmountOnExit?: boolean;
  className?: string;
} & React.HTMLAttributes<HTMLElement>;

/**
 * Group (nested items) renderer.
 *
 * This used to be a react-spring animated MUI Collapse, which meant one spring
 * per expandable row driving a JS animation every frame. Expanding a large tree
 * started hundreds of springs at once and the whole UI stalled. MUI already
 * unmounts collapsed groups (`unmountOnExit`), so all this needs to do is
 * render the list when the item is expanded; the reveal is a cheap
 * compositor-only CSS animation defined in TreeItemGroup.scss.
 */
export const TreeItemGroup = React.memo(function TreeItemGroup({
  in: inProp,
  children,
  component,
  // Consumed by MUI's Collapse API; must not reach the DOM node.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  unmountOnExit,
  className,
  ...other
}: TreeItemGroupProps) {
  if (!inProp) return null;
  const Component = (component ?? 'ul') as React.ElementType;
  return (
    <Component
      {...other}
      className={className ? `tree-item-group ${className}` : 'tree-item-group'}
    >
      {children}
    </Component>
  );
});
